import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { orderAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'sonner';
import {
  Zap, Home, Settings, LogOut, Menu, Package, ClipboardList, Eye, Truck,
  CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, CreditCard
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Orders' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  processing: 'bg-blue-100 text-blue-700',
  shipped: 'bg-purple-100 text-purple-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

const AdminOrdersPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => { fetchOrders(); }, [statusFilter]);

  const fetchOrders = async () => {
    try {
      const res = await orderAPI.adminList(statusFilter === 'all' ? null : statusFilter);
      setOrders(res.data);
    } catch (error) { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await orderAPI.adminUpdateStatus(orderId, newStatus);
      setOrders(orders.map(o => o.order_id === orderId ? { ...o, status: newStatus } : o));
      toast.success(`Order updated to ${newStatus}`);
    } catch (error) { toast.error('Failed to update status'); }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const navItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    { icon: <Zap className="w-5 h-5" />, label: 'Admin', path: '/admin' },
    { icon: <CreditCard className="w-5 h-5" />, label: 'Artifacts', path: '/admin/manage-artifacts' },
    { icon: <Package className="w-5 h-5" />, label: 'Catalog', path: '/admin/artifacts' },
    { icon: <ClipboardList className="w-5 h-5" />, label: 'Orders', path: '/admin/orders', active: true },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
  ];

  const totalRevenue = orders.reduce((sum, o) => o.status !== 'cancelled' ? sum + (o.total_price || 0) : sum, 0);
  const pendingCount = orders.filter(o => o.status === 'pending').length;

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center"><Zap className="w-5 h-5 text-white" /></div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
          </Link>
        </div>
        <nav className="flex-1 p-4">
          <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">Admin</p>
          <ul className="space-y-1">
            {navItems.map((item, i) => (
              <li key={i}>
                <Link to={item.path} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${location.pathname === item.path ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-100'}`}>
                  {item.icon}<span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={handleLogout}>
            <LogOut className="w-5 h-5 mr-3" />Sign out
          </Button>
        </div>
      </aside>

      <div className="dashboard-content">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 h-16">
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Orders Management</h1>
            <span className="text-sm px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full font-medium">Admin</span>
          </div>
        </header>

        <main className="p-6" data-testid="admin-orders-page">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-2xl font-bold text-slate-900">{orders.length}</p>
              <p className="text-sm text-slate-500">Total Orders</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
              <p className="text-sm text-slate-500">Pending</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-2xl font-bold text-emerald-600">₱{totalRevenue.toLocaleString()}</p>
              <p className="text-sm text-slate-500">Total Revenue</p>
            </div>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-3 mb-4">
            <Label className="text-sm text-slate-600">Filter:</Label>
            <div className="flex gap-1">
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => { setStatusFilter(opt.value); setLoading(true); }}
                  className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${statusFilter === opt.value ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  data-testid={`filter-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Orders List */}
          <div className="space-y-3">
            {orders.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 px-6 py-12 text-center">
                <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No orders found</p>
              </div>
            ) : orders.map(order => (
              <div key={order.order_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`order-${order.order_id}`}>
                <button
                  onClick={() => setExpandedOrder(expandedOrder === order.order_id ? null : order.order_id)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-left">
                      <p className="font-semibold text-slate-900">{order.order_id}</p>
                      <p className="text-sm text-slate-500">{order.customer_name} &bull; {new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="font-semibold text-slate-900">₱{order.total_price?.toLocaleString()}</p>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize ${STATUS_COLORS[order.status] || 'bg-slate-100 text-slate-600'}`}>
                      {order.status}
                    </span>
                    {expandedOrder === order.order_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>

                {expandedOrder === order.order_id && (
                  <div className="border-t border-slate-200 px-6 py-4">
                    <div className="grid sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-slate-500 font-medium mb-1">Customer</p>
                        <p className="text-sm text-slate-900">{order.customer_name}</p>
                        <p className="text-sm text-slate-600">{order.email}</p>
                        <p className="text-sm text-slate-600">{order.phone}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 font-medium mb-1">Shipping Address</p>
                        <p className="text-sm text-slate-900">{order.shipping_address}</p>
                        {order.notes && <p className="text-sm text-slate-500 mt-1">Note: {order.notes}</p>}
                      </div>
                    </div>
                    <div className="mb-4">
                      <p className="text-xs text-slate-500 font-medium mb-2">Items</p>
                      <div className="space-y-1">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm">
                            <span className="text-slate-900">{item.name} x{item.quantity}</span>
                            <span className="font-medium text-slate-700">₱{(item.price * item.quantity).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-slate-600">Update Status:</Label>
                      {['pending', 'processing', 'shipped', 'completed', 'cancelled'].map(s => (
                        <button
                          key={s}
                          onClick={() => handleUpdateStatus(order.order_id, s)}
                          disabled={order.status === s}
                          className={`px-3 py-1 text-xs rounded-full font-medium transition-colors capitalize ${order.status === s ? STATUS_COLORS[s] : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                          data-testid={`status-${s}-${order.order_id}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminOrdersPage;
